import { useState, useEffect } from "react";
import { addNotification } from "../../utils/notifications";
import { sendResendEmail, buildRewardOutcomeEmail } from "../../utils/email";
import { EmojiPicker } from "../shared/EmojiPicker";
import { CategoryManager } from "./CategoryManager";
import { getCategories, fbGet, fbSet, DEMO_MODE } from "../../utils/firebase";

import iconSpelling from "../../assets/icons/icon-spelling.webp";
import iconMeanings from "../../assets/icons/icon-meanings.webp";
import iconSynonyms from "../../assets/icons/icon-synonyms.webp";
import iconAntonyms from "../../assets/icons/icon-antonyms.webp";

const SESSION_ICON = { spelling: iconSpelling, meaning: iconMeanings, synonym: iconSynonyms, antonym: iconAntonyms };
const CHILD_COLORS = ["#7c3aed", "#0891b2", "#16a34a", "#f59e0b"];
const CHILD_BG     = ["#ede9fe", "#cffafe", "#dcfce7", "#fef3c7"];

export function PlannerRewards({ data, users, onDataChange, settings }) {
  const children = users.filter(u => u.role === "child");
  const parentId = users.find(u => u.role === "parent")?.id || "parent";

  // ── Reward shop state ──────────────────────────────────────────────────────
  const [categories, setCategories]         = useState([]);
  const [newName, setNewName]               = useState("");
  const [newEmoji, setNewEmoji]             = useState("🎁");
  const [newCost, setNewCost]               = useState("");
  const [newCategoryId, setNewCategoryId]   = useState("");
  const [newCatError, setNewCatError]       = useState(false);
  const [showNew, setShowNew]               = useState(false);
  const [noteInputs, setNoteInputs]         = useState({});
  const [adjustCosts, setAdjustCosts]       = useState({});
  const [editingId, setEditingId]           = useState(null);
  const [editName, setEditName]             = useState("");
  const [editEmoji, setEditEmoji]           = useState("");
  const [editCost, setEditCost]             = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editCatError, setEditCatError]     = useState(false);

  // ── Quick actions state ────────────────────────────────────────────────────
  const [savedActionsMap, setSavedActionsMap] = useState({});
  const [showAddForm, setShowAddForm]         = useState(false);
  const [formChildId, setFormChildId]         = useState("");
  const [editActionId, setEditActionId]       = useState(null);
  const [formLabel, setFormLabel]             = useState("");
  const [formAmount, setFormAmount]           = useState("");
  const [formType, setFormType]               = useState("bonus");
  const [customAmts, setCustomAmts]           = useState({});
  const [quickMsgs, setQuickMsgs]             = useState({});

  // ── History state ──────────────────────────────────────────────────────────
  const [historyFilter, setHistoryFilter] = useState("all");

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { getCategories().then(setCategories); }, []);

  useEffect(() => {
    async function load() {
      if (DEMO_MODE) {
        try {
          const raw = localStorage.getItem(`savedActions_${parentId}`);
          if (raw) setSavedActionsMap(JSON.parse(raw));
        } catch {}
        return;
      }
      const val = await fbGet(`savedActions/${parentId}`);
      if (val && typeof val === "object") setSavedActionsMap(val);
    }
    load();
  }, [parentId]);

  // ── Points helpers ─────────────────────────────────────────────────────────
  function applyPoints(childId, delta, reason) {
    const adj = { id: `adj_${Date.now()}`, userId: childId, delta, reason, date: Date.now() };
    const newPoints = { ...(data.points || {}), [childId]: Math.max(0, (data.points?.[childId] || 0) + delta) };
    const newAdj = [...(data.pointAdjustments || []), adj];
    let newData = { ...data, points: newPoints, pointAdjustments: newAdj };
    newData = addNotification(newData, childId, {
      icon: delta > 0 ? "⭐" : "⚠️",
      title: delta > 0 ? `+${delta} Bonus Points!` : `−${Math.abs(delta)} Points Deducted`,
      body: reason || (delta > 0 ? "Bonus from your parents" : "Deduction applied"),
    });
    onDataChange(newData);
  }

  function applyBonus(amt, reason, childId) {
    const n = parseInt(amt);
    if (!n || n <= 0) return;
    applyPoints(childId, n, reason || "Bonus");
    const name = children.find(c => c.id === childId)?.name || "";
    setQuickMsgs(m => ({ ...m, [childId]: `+${n} pts added to ${name} ✓` }));
    setTimeout(() => setQuickMsgs(m => { const c = { ...m }; delete c[childId]; return c; }), 2500);
  }

  function applyDeduction(amt, reason, childId) {
    const n = parseInt(amt);
    if (!n || n <= 0) return;
    applyPoints(childId, -n, reason || "Deduction");
    const name = children.find(c => c.id === childId)?.name || "";
    setQuickMsgs(m => ({ ...m, [childId]: `−${n} pts from ${name} ✓` }));
    setTimeout(() => setQuickMsgs(m => { const c = { ...m }; delete c[childId]; return c; }), 2500);
  }

  // ── Saved actions helpers ──────────────────────────────────────────────────
  async function persistActionsForChild(childId, actions) {
    const newMap = { ...savedActionsMap, [childId]: actions };
    setSavedActionsMap(newMap);
    if (DEMO_MODE) {
      try { localStorage.setItem(`savedActions_${parentId}`, JSON.stringify(newMap)); } catch {}
    } else {
      await fbSet(`savedActions/${parentId}/${childId}`, actions);
    }
  }

  function resetForm() {
    setFormLabel(""); setFormAmount(""); setFormType("bonus");
    setEditActionId(null); setShowAddForm(false); setFormChildId("");
  }

  function handleAddSavedAction() {
    if (!formLabel.trim() || !formAmount || parseInt(formAmount) < 1 || !formChildId) return;
    const current = savedActionsMap[formChildId] || [];
    if (current.length >= 6) return;
    const action = { id: `sa_${Date.now()}`, label: formLabel.trim(), amount: parseInt(formAmount), type: formType };
    persistActionsForChild(formChildId, [...current, action]);
    resetForm();
  }

  function startEditAction(action, childId) {
    setFormChildId(childId);
    setEditActionId(action.id);
    setFormLabel(action.label);
    setFormAmount(String(action.amount));
    setFormType(action.type);
    setShowAddForm(true);
  }

  function handleSaveEditAction() {
    if (!formLabel.trim() || !formAmount || parseInt(formAmount) < 1 || !formChildId) return;
    const current = savedActionsMap[formChildId] || [];
    const updated = current.map(a => a.id === editActionId
      ? { ...a, label: formLabel.trim(), amount: parseInt(formAmount), type: formType }
      : a);
    persistActionsForChild(formChildId, updated);
    resetForm();
  }

  function deleteSavedAction(actionId) {
    const current = savedActionsMap[formChildId] || [];
    persistActionsForChild(formChildId, current.filter(a => a.id !== actionId));
    resetForm();
  }

  function applyAction(action, childId) {
    if (action.type === "bonus") applyBonus(action.amount, action.label, childId);
    else applyDeduction(action.amount, action.label, childId);
  }

  // ── Reward shop helpers ────────────────────────────────────────────────────
  function addReward() {
    const cost = parseInt(newCost, 10);
    if (!newName.trim() || !cost || cost < 1) return;
    if (!newCategoryId) { setNewCatError(true); return; }
    const r = { id: `r_${Date.now()}`, name: newName.trim(), emoji: newEmoji || "🎁", cost, categoryId: newCategoryId };
    onDataChange({ ...data, rewards: [...(data.rewards || []), r] });
    setNewName(""); setNewCost(""); setNewEmoji("🎁"); setNewCategoryId(""); setNewCatError(false); setShowNew(false);
  }

  function delReward(id) { onDataChange({ ...data, rewards: (data.rewards || []).filter(r => r.id !== id) }); }

  function startEdit(r) {
    setEditingId(r.id); setEditName(r.name); setEditEmoji(r.emoji);
    setEditCost(String(r.cost)); setEditCategoryId(r.categoryId || ""); setEditCatError(false);
  }

  function saveEdit(id) {
    if (!editName.trim() || !editCost) return;
    if (!editCategoryId) { setEditCatError(true); return; }
    onDataChange({ ...data, rewards: (data.rewards || []).map(r => r.id === id ? { ...r, name: editName.trim(), emoji: editEmoji, cost: parseInt(editCost), categoryId: editCategoryId } : r) });
    setEditingId(null); setEditCatError(false);
  }

  function handleRequest(reqId, action, overrideCost) {
    const req = (data.requests || []).find(r => r.id === reqId);
    if (!req) return;
    const finalCost = overrideCost !== undefined ? parseInt(overrideCost) : req.cost;
    const note = noteInputs[reqId] || "";
    let newPoints = { ...(data.points || {}) };
    if (action === "approved") newPoints[req.userId] = Math.max(0, (newPoints[req.userId] || 0) - finalCost);
    const updatedReqs = (data.requests || []).map(r => r.id === reqId ? { ...r, status: action, cost: finalCost, parentNote: note } : r);
    let newData = { ...data, requests: updatedReqs, points: newPoints };
    newData = addNotification(newData, req.userId, {
      icon: action === "approved" ? "🎉" : "😔",
      title: action === "approved" ? "Reward Approved!" : "Reward Not Approved",
      body: `${req.rewardEmoji || "🎁"} ${req.rewardName}${note ? ` — "${note}"` : ""}`,
    });
    onDataChange(newData);
    setNoteInputs(n => { const c = { ...n }; delete c[reqId]; return c; });
    setAdjustCosts(a => { const c = { ...a }; delete c[reqId]; return c; });
    const resend = settings?.resend || {};
    if (resend.apiKey && resend.fromEmail) {
      const child = users.find(u => u.id === req.userId);
      const childEmail = settings?.childEmails?.[req.userId];
      if (child && childEmail) {
        sendResendEmail(resend.apiKey, resend.fromEmail, {
          to: childEmail,
          subject: `SpellWise Kids — ${action === "approved" ? "🎉 Reward Approved!" : "Reward Update"}`,
          html: buildRewardOutcomeEmail({ child, reward: { name: req.rewardName, emoji: req.rewardEmoji, cost: finalCost }, approved: action === "approved", parentNote: note }),
        });
      }
    }
  }

  // ── Derived data ───────────────────────────────────────────────────────────
  const pendingRequests  = (data.requests || []).filter(r => r.status === "pending");
  const allRewardsSorted = [...(data.rewards || [])].sort((a, b) => a.cost - b.cost);

  function nextRewardFor(pts) { return allRewardsSorted.find(r => pts < r.cost) || null; }

  const allActivity = [
    ...(data.sessions || []).map(s => ({ id: s.id, date: s.date, userId: s.userId, kind: "session", sessionType: s.type, ptsEarned: s.ptsEarned || 0 })),
    ...(data.requests || []).filter(r => r.status !== "pending").map(r => ({ id: r.id, date: r.date, userId: r.userId, kind: "request", status: r.status, rewardName: r.rewardName, rewardEmoji: r.rewardEmoji, cost: r.cost })),
    ...(data.pointAdjustments || []).map(a => ({ id: a.id, date: a.date, userId: a.userId, kind: "adjustment", delta: a.delta, reason: a.reason })),
  ].sort((a, b) => b.date - a.date);

  const filteredActivity = historyFilter === "all" ? allActivity : allActivity.filter(a => a.userId === historyFilter);

  function sessionLabel(type) {
    if (type === "meaning") return "Meaning Quiz";
    if (type === "synonym") return "Synonyms Quiz";
    if (type === "antonym") return "Antonyms Quiz";
    return "Spelling Test";
  }
  function sessionIconKey(type) {
    if (type === "meaning") return "meaning";
    if (type === "synonym") return "synonym";
    if (type === "antonym") return "antonym";
    return "spelling";
  }

  return (
    <>
      {/* ── 1. PAGE HEADER ──────────────────────────────────────────────────── */}
      <div className="mb-5">
        <div className="font-handwritten text-3xl text-stone-800">Rewards Manager</div>
        <div className="text-sm text-stone-500 mt-1">Manage points, bonuses and reward requests</div>
      </div>

      {/* ── 2. POINTS OVERVIEW ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        {children.map((c, i) => {
          const pts    = data.points?.[c.id] || 0;
          const streak = data.streaks?.[c.id] || 0;
          const next   = nextRewardFor(pts);
          const pct    = next ? Math.min(99, Math.round((pts / next.cost) * 100)) : 100;
          const tapeColor = i === 0 ? "bg-pink-200" : "bg-blue-200";
          return (
            <div key={c.id} className="index-card rounded-xl p-5 hover-lift relative">
              <div className={`absolute -top-2 left-4 w-12 h-4 tape rounded washi-stripe ${tapeColor}`} />
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{c.avatar}</span>
                <div>
                  <div className="font-bold text-sm">{c.name}</div>
                  <div className="text-xs text-stone-500">Year {c.yearLevel}</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-amber-600 leading-tight mb-2">⭐ {pts}</div>
              <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 rounded-full px-2 py-0.5 text-xs font-medium mb-3">
                🔥 {streak} day streak
              </div>
              <div className="bg-stone-200 rounded-full h-2 overflow-hidden mb-2">
                <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${pct}%`, transition: "width .4s" }} />
              </div>
              <div className="text-xs text-stone-500 font-semibold">
                {next ? <>{next.cost - pts} pts to <strong className="text-stone-700">{next.emoji} {next.name}</strong></> : "🎉 Can afford everything!"}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 3. QUICK ACTIONS ────────────────────────────────────────────────── */}
      <div className="index-card rounded-xl p-5 mb-6">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">⚡ Quick Actions</div>

        <div className="grid grid-cols-2 gap-3">
          {children.map((c) => {
            const actions = savedActionsMap[c.id] || [];
            const slots   = [...actions, ...Array(Math.max(0, 6 - actions.length)).fill(null)].slice(0, 6);
            const openAdd = () => { resetForm(); setFormChildId(c.id); setShowAddForm(true); };
            return (
              <div key={c.id}>
                {/* Child header */}
                <div className="font-bold text-sm mb-2 flex items-center gap-1">
                  {c.avatar} {c.name}
                </div>

                {/* 3 rows × 2 cols grid */}
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {slots.map((action, idx) => {
                    if (!action) {
                      return (
                        <div key={`empty-${idx}`} onClick={openAdd}
                          className="border-2 border-dashed border-stone-300 rounded-lg h-16 flex items-center justify-center cursor-pointer text-stone-400 text-xs font-semibold">
                          + Add
                        </div>
                      );
                    }
                    const isBonus = action.type === "bonus";
                    return (
                      <div key={action.id}
                        className="sticker rounded-lg h-16 flex flex-col items-center justify-center cursor-pointer overflow-hidden text-white text-sm font-bold relative"
                        style={{ background: isBonus ? "linear-gradient(135deg,#16a34a,#22c55e)" : "linear-gradient(135deg,#dc2626,#ef4444)" }}
                        onClick={() => applyAction(action, c.id)}>
                        <div className="text-base font-black">{isBonus ? "+" : "−"}{action.amount}</div>
                        <div className="text-xs opacity-90 mt-0.5 text-center px-3 line-clamp-2">{action.label}</div>
                        <button onClick={e => { e.stopPropagation(); startEditAction(action, c.id); }}
                          className="absolute top-1 right-1 bg-black/20 border-none rounded w-4.5 h-4.5 cursor-pointer text-xs text-white flex items-center justify-center p-0 hover:bg-black/30">
                          ✏
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Feedback message */}
                {quickMsgs[c.id] && (
                  <div className={`text-xs font-bold mb-2 ${quickMsgs[c.id].startsWith("+") ? "text-green-600" : "text-red-600"}`}>
                    {quickMsgs[c.id]}
                  </div>
                )}

                {/* Custom amount row */}
                <div className="flex gap-1">
                  <input type="number" min="1" placeholder="Pts"
                    value={customAmts[c.id] || ""}
                    onChange={e => setCustomAmts(m => ({ ...m, [c.id]: e.target.value }))}
                    className="flex-1 px-2 py-1 border border-stone-300 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                  <button onClick={() => { applyBonus(customAmts[c.id], "Custom bonus", c.id); setCustomAmts(m => ({ ...m, [c.id]: "" })); }}
                    disabled={!customAmts[c.id] || parseInt(customAmts[c.id]) < 1}
                    className="px-2 py-1 bg-green-600 text-white rounded text-sm font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700">+</button>
                  <button onClick={() => { applyDeduction(customAmts[c.id], "Custom deduction", c.id); setCustomAmts(m => ({ ...m, [c.id]: "" })); }}
                    disabled={!customAmts[c.id] || parseInt(customAmts[c.id]) < 1}
                    className="px-2 py-1 bg-red-600 text-white rounded text-sm font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700">−</button>
                </div>

                {/* Inline add/edit form for this child */}
                {showAddForm && formChildId === c.id && (
                  <div className="bg-stone-100 rounded-lg p-3 mt-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button onClick={() => setFormType("bonus")}
                          className={`flex-1 px-2 py-1 rounded text-xs font-bold cursor-pointer border-2 ${formType === "bonus" ? "border-green-600 bg-green-100 text-green-600" : "border-stone-300 bg-stone-50 text-stone-600"}`}>
                          + Bonus
                        </button>
                        <button onClick={() => setFormType("deduction")}
                          className={`flex-1 px-2 py-1 rounded text-xs font-bold cursor-pointer border-2 ${formType === "deduction" ? "border-red-600 bg-red-100 text-red-600" : "border-stone-300 bg-stone-50 text-stone-600"}`}>
                          − Deduct
                        </button>
                      </div>
                      <input type="number" min="1" placeholder="Amount (pts)" value={formAmount} onChange={e => setFormAmount(e.target.value)}
                        className="px-2 py-1 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                      <input placeholder="Label (e.g. Homework done)" value={formLabel} onChange={e => setFormLabel(e.target.value)}
                        className="px-2 py-1 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                      <div className="flex gap-1.5">
                        <button onClick={editActionId ? handleSaveEditAction : handleAddSavedAction}
                          disabled={!formLabel.trim() || !formAmount || parseInt(formAmount) < 1}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700">
                          {editActionId ? "Save" : "Add"}
                        </button>
                        {editActionId && (
                          <button className="px-2 py-1 bg-red-600 text-white rounded text-xs font-bold cursor-pointer hover:bg-red-700"
                            onClick={() => deleteSavedAction(editActionId)}>Delete</button>
                        )}
                        <button onClick={resetForm} className="px-3 py-1 bg-stone-300 text-stone-700 rounded text-xs font-bold cursor-pointer hover:bg-stone-400">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── PENDING REQUESTS ─────────────────────────────────────────────────── */}
      <div className="sticky-orange rounded-xl p-5 shadow-md mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-handwritten text-2xl text-stone-800">📬 Pending Requests</span>
          {pendingRequests.length > 0 && <span className="w-6 h-6 bg-red-500 text-white text-sm rounded-full flex items-center justify-center font-bold">{pendingRequests.length}</span>}
        </div>
        {pendingRequests.length === 0
          ? <div className="text-stone-500 text-sm">No pending requests — all caught up! 🎉</div>
          : pendingRequests.map(r => {
            const child = children.find(c => c.id === r.userId);
            const adjustedCost = adjustCosts[r.id] !== undefined ? adjustCosts[r.id] : r.cost;
            return (
              <div key={r.id} className="bg-white/80 rounded-lg p-4 mb-3">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-bold text-sm">{r.rewardEmoji} {r.rewardName}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      {child?.avatar} {r.userName} · {new Date(r.date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                      {r.custom && <span className="ml-2 font-bold">(custom)</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold text-stone-500">Points cost:</span>
                  <input type="number" min="0" value={adjustedCost}
                    onChange={e => setAdjustCosts(a => ({ ...a, [r.id]: e.target.value }))}
                    className="w-20 px-2 py-1 border border-stone-300 rounded text-center text-sm font-bold focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <input placeholder="Optional note to child…" value={noteInputs[r.id] || ""}
                  onChange={e => setNoteInputs(n => ({ ...n, [r.id]: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-3" />
                <div className="flex gap-2">
                  <button className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-600 cursor-pointer" onClick={() => handleRequest(r.id, "approved", adjustedCost)}>✅ Approve</button>
                  <button className="flex-1 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-bold hover:bg-red-200 cursor-pointer" onClick={() => handleRequest(r.id, "declined", adjustedCost)}>❌ Decline</button>
                </div>
              </div>
            );
          })}
      </div>

      {/* ── REWARD SHOP ──────────────────────────────────────────────────────── */}
      <div className="sticky-purple rounded-xl p-5 shadow-md mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="font-handwritten text-2xl text-stone-800">🛍️ Reward Shop</div>
          <button className="text-purple-600 hover:text-purple-800 font-bold text-sm cursor-pointer"
            onClick={() => setShowNew(s => !s)}>{showNew ? "Cancel" : "+ Add Reward"}</button>
        </div>

        {/* Add form */}
        {showNew && (
          <div className="bg-stone-100 rounded-lg p-4 mb-4">
            <div className="flex flex-col gap-3">
              <div className="flex gap-2 items-center">
                <EmojiPicker value={newEmoji} onChange={setNewEmoji} />
                <input placeholder="Reward name" value={newName} onChange={e => setNewName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              </div>
              <input type="number" placeholder="Point cost" min="1" value={newCost} onChange={e => setNewCost(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 max-w-xs" />
              <div>
                <label className="text-xs font-bold text-stone-500 block mb-1">Category (required)</label>
                <select value={newCategoryId} onChange={e => { setNewCategoryId(e.target.value); setNewCatError(false); }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
                  <option value="">Select a category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                </select>
                {newCatError && <div className="text-red-600 text-xs font-bold mt-1">Please select a category</div>}
              </div>
              <button onClick={addReward} disabled={!newName.trim() || !newCost || parseInt(newCost, 10) < 1}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700">Add to Shop</button>
            </div>
          </div>
        )}

        {/* 2-col reward grid */}
        {(data.rewards || []).length === 0
          ? <div className="text-stone-500 text-sm">No rewards yet — add some above!</div>
          : (
            <div className="grid grid-cols-2 gap-2.5">
              {[...(data.rewards || [])].sort((a, b) => b.cost - a.cost).map(r => {
                const cat       = categories.find(c => c.id === r.categoryId);
                const isEditing = editingId === r.id;
                return (
                  <div key={r.id} className={`${isEditing ? "col-span-2" : "col-span-1"} bg-white/80 rounded-lg overflow-hidden flex flex-col border border-stone-200`}>
                    {!isEditing ? (
                      <>
                        <div className="h-14 flex items-center justify-center bg-stone-100 text-2xl flex-shrink-0">
                          {r.emoji}
                        </div>
                        <div className="p-3 flex-1 flex flex-col gap-1">
                          <div className="font-bold text-sm leading-snug">{r.name}</div>
                          <div className="text-xs text-amber-600 font-bold">🌟 {r.cost} pts</div>
                          {cat && <span className="text-xs font-bold text-pink-700 bg-pink-100 rounded px-2 py-0.5 inline-block">{cat.emoji} {cat.name}</span>}
                        </div>
                        <div className="flex gap-1.5 p-2">
                          <button className="flex-1 px-2 py-1 bg-stone-200 text-stone-700 rounded text-xs font-bold cursor-pointer hover:bg-stone-300"
                            onClick={() => startEdit(r)}>✏️</button>
                          <button className="flex-1 px-2 py-1 bg-red-200 text-red-700 rounded text-xs font-bold cursor-pointer hover:bg-red-300"
                            onClick={() => delReward(r.id)}>✕</button>
                        </div>
                      </>
                    ) : (
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="font-bold text-sm">Edit Reward</div>
                          <button className="px-2 py-1 bg-stone-200 text-stone-700 rounded text-xs font-bold cursor-pointer hover:bg-stone-300"
                            onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                        <div className="flex flex-col gap-3">
                          <div className="flex gap-2 items-center">
                            <EmojiPicker value={editEmoji} onChange={setEditEmoji} />
                            <input placeholder="Reward name" value={editName} onChange={e => setEditName(e.target.value)}
                              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                          </div>
                          <input type="number" placeholder="Point cost" min="1" value={editCost} onChange={e => setEditCost(e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 max-w-xs" />
                          <div>
                            <label className="text-xs font-bold text-stone-500 block mb-1">Category (required)</label>
                            <select value={editCategoryId} onChange={e => { setEditCategoryId(e.target.value); setEditCatError(false); }}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
                              <option value="">Select a category...</option>
                              {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                            </select>
                            {editCatError && <div className="text-red-600 text-xs font-bold mt-1">Please select a category</div>}
                          </div>
                          <button onClick={() => saveEdit(r.id)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold cursor-pointer hover:bg-purple-700">Save</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* ── REWARD CATEGORIES ────────────────────────────────────────────────── */}
      <CategoryManager data={data} />

      {/* ── ACTIVITY HISTORY ─────────────────────────────────────────────────── */}
      <div className="bg-white/80 rounded-xl border border-stone-200 p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-3">Activity History</div>
        <div className="flex gap-1.5 mb-4 flex-wrap">
          <button onClick={() => setHistoryFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition ${historyFilter === "all" ? "washi-stripe bg-amber-500 text-white" : "washi-stripe bg-stone-100 text-stone-600"}`}>All</button>
          {children.map(c => (
            <button key={c.id} onClick={() => setHistoryFilter(c.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition flex items-center gap-1 washi-stripe ${historyFilter === c.id ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600"}`}>
              {c.avatar} {c.name}
            </button>
          ))}
        </div>
        {filteredActivity.length === 0
          ? <div className="text-stone-500 text-sm">No activity yet.</div>
          : filteredActivity.slice(0, 40).map(a => {
            const childIdx   = children.findIndex(c => c.id === a.userId);
            const child      = children[childIdx];
            const badgeColor = CHILD_COLORS[childIdx] || "#64748b";
            const badgeBg    = CHILD_BG[childIdx]    || "#f1f5f9";
            let icon, label, right;

            if (a.kind === "session") {
              const ik = sessionIconKey(a.sessionType);
              icon  = SESSION_ICON[ik] ? <img src={SESSION_ICON[ik]} alt="" className="w-6 h-6 rounded object-cover" /> : <span className="text-lg">📝</span>;
              label = sessionLabel(a.sessionType);
              right = <div className="font-bold text-sm text-green-600 whitespace-nowrap">+{a.ptsEarned} pts</div>;
            } else if (a.kind === "request") {
              icon  = <span className="text-xl">{a.rewardEmoji || "🎁"}</span>;
              label = a.rewardName;
              right = a.status === "approved"
                ? <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">✓ Approved</span>
                : <span className="bg-cyan-100 text-cyan-700 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">↩ Returned to Bank</span>;
            } else {
              icon  = <span className="text-xl">{a.delta > 0 ? "⭐" : "⚠️"}</span>;
              label = a.reason || (a.delta > 0 ? "Bonus points" : "Point deduction");
              right = a.delta > 0
                ? <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">⭐ Bonus</span>
                : <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">⚠️ Deduction</span>;
            }

            return (
              <div key={a.id} className="flex items-center gap-3 py-3 px-2 border-b border-stone-200">
                <div className="flex-shrink-0 w-7 flex justify-center">{icon}</div>
                <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black" style={{ backgroundColor: badgeBg, color: badgeColor }}>
                  {child?.name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm overflow-hidden text-ellipsis whitespace-nowrap">{label}</div>
                  <div className="text-xs text-stone-500">
                    {child?.name} · {new Date(a.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <div className="flex-shrink-0">{right}</div>
              </div>
            );
          })}
      </div>
    </>
  );
}
